import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist/legacy/build/pdf.mjs';
import { PDFDocument, StandardFonts, rgb, degrees, LineCapStyle } from 'pdf-lib';
import { calculateMarkingTotals } from '../utils/gradingPayload';
import { isRubricTask, buildRubricStamps } from '../utils/rubricAnnotations';
import { collectInlineAnnotations, locateInlineInText, buildInlineBadges, viewportToPdfPoint } from '../utils/inlineProofreading';
import { assignRowsInOrder, clearPrintedText, enclosedRows, findInkUnits, findRuledLines, hasPenInk, inkMaskFromRgba, penMaskFromRgba, rowsBelow, scanSearchArea, tickPositions } from '../utils/tickPlacement';
import { questionAnchorKey, indexPrintedMarkAnchors, printedAnchorFor, ensureGradedItemAnnotations, withReviewStripFallback, questionPaperPage } from '../utils/printedMarkAnchors';

GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
const EMPTY_ANNOTATIONS = [];

export function isPdf(file) {
    return file?.type === 'application/pdf' || file?.name?.toLowerCase().endsWith('.pdf');
}

export function getTickCoordinates(annotation) {
    if (!annotation || annotation.needsPlacement) {
        return null;
    }
    const rawX = annotation.tickPoint?.x ?? annotation.x;
    const rawY = annotation.tickPoint?.y ?? annotation.y;
    if (rawX === null || rawX === undefined || rawY === null || rawY === undefined || rawX === '' || rawY === '') {
        return null;
    }
    const numX = Number(rawX);
    const numY = Number(rawY);
    if (!Number.isFinite(numX) || !Number.isFinite(numY)) {
        return null;
    }
    return {
        x: Math.min(/^(?:pdf_printed_anchor|page_review_strip)$/.test(annotation.sourceCoordinate || '') ? 20 : 1, Math.max(0, numX)),
        y: Math.min(1, Math.max(0, numY)),
    };
}

export function getNormalizedEvidenceBox(annotation) {
    const box = annotation?.evidenceBox;
    if (!box || typeof box !== 'object') {
        return null;
    }
    const x = Number(box.x);
    const y = Number(box.y);
    const width = Number(box.width);
    const height = Number(box.height);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) {
        return null;
    }
    if (width <= 0 || height <= 0) {
        return null;
    }
    return {
        x: Math.min(1, Math.max(0, x)),
        y: Math.min(1, Math.max(0, y)),
        width: Math.min(1, Math.max(0, width)),
        height: Math.min(1, Math.max(0, height)),
    };
}

/**
 * Converts normalized top-left page coordinates (0..1, matching PDF.js viewport & SVG viewBox="0 0 1 1")
 * into pdf-lib page coordinates (bottom-left origin, respecting page CropBox origin and size).
 */
export function normalizedToPdfPagePoint(coordinates, pageBox) {
    const originX = Number(pageBox?.x || 0);
    const originY = Number(pageBox?.y || 0);
    const width = Number(pageBox?.width || 0);
    const height = Number(pageBox?.height || 0);
    return {
        x: originX + (coordinates.x * width),
        y: originY + (height - (coordinates.y * height)),
    };
}

/**
 * Rasterizes each page of a learner PDF at a fast, deterministic resolution using PDF.js
 * so the backend sends Gemini the exact rendered page images that match the viewer.
 * Protected by a strict timeout so it never blocks submission if the worker is unavailable.
 */
export async function rasterizeLearnerDocument(file) {
    if (!file || typeof document === 'undefined' || !isPdf(file) || typeof file.arrayBuffer !== 'function') {
        return { pages: [], dimensions: [] };
    }

    const rasterPromise = (async () => {
        let pdfDoc = null;
        try {
            const buffer = await file.arrayBuffer();
            const bytes = new Uint8Array(buffer);
            const header = new TextDecoder().decode(bytes.slice(0, 5));
            if (header !== '%PDF-') {
                return { pages: [], dimensions: [] };
            }
            pdfDoc = await getDocument({
                data: bytes,
                useWorkerFetch: false,
                isEvalSupported: false,
            }).promise;

            const pages = [];
            const dimensions = [];
            const totalPages = pdfDoc.numPages || 0;
            // If rasterization is unsuitable, the caller sends the complete PDF.
            // Never send a silently truncated or partially rendered script.
            if (totalPages > 100) return { pages: [], dimensions: [] };

            for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
                const page = await pdfDoc.getPage(pageNumber);
                const baseViewport = page.getViewport({ scale: 1 });
                const targetWidth = 900;
                const scale = Math.max(1.0, Math.min(1.5, targetWidth / Math.max(1, baseViewport.width)));
                const viewport = page.getViewport({ scale });
                const width = Math.round(viewport.width);
                const height = Math.round(viewport.height);

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const context = canvas.getContext('2d');
                if (!context) {
                    return { pages: [], dimensions: [] };
                }
                context.fillStyle = '#ffffff';
                context.fillRect(0, 0, width, height);
                await page.render({ canvasContext: context, viewport }).promise;

                const blob = await new Promise((resolveBlob) => {
                    if (typeof canvas.toBlob === 'function') {
                        canvas.toBlob((result) => resolveBlob(result), 'image/jpeg', 0.72);
                    } else {
                        resolveBlob(null);
                    }
                });

                dimensions.push({ page: pageNumber, width, height });
                if (!blob) return { pages: [], dimensions: [] };
                if (blob) {
                    pages.push({
                        page: pageNumber,
                        width,
                        height,
                        blob,
                        fileName: `learner-page-${pageNumber}.jpg`,
                    });
                }
            }

            return { pages, dimensions };
        } catch {
            return { pages: [], dimensions: [] };
        } finally {
            if (pdfDoc && typeof pdfDoc.destroy === 'function') {
                try {
                    pdfDoc.destroy();
                } catch {
                    // ignore cleanup error
                }
            }
        }
    })();

    const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve({ pages: [], dimensions: [] }), 8000);
    });

    return Promise.race([rasterPromise, timeoutPromise]);
}

function normalizeQuestionLabel(value) {
    return questionAnchorKey(value);
}

/**
 * Deterministic tick placement for PDFs with a text layer. LLM-estimated coordinates are
 * unreliable, so we locate each question label ("1.3.3") with PDF.js text positions, take the
 * rows belonging to that question, and place marks just right of the real text on those lines.
 * When an answer is handwriting or a drawing, preserve validated visual evidence
 * positions: a printed question does not imply its answer exists in the text layer.
 * Tier 2 uses printed question numbers and question-associated mark brackets.
 * The viewer/export add a labelled review strip if neither tier can be verified.
 */
// Pages are drawn once per file: placement runs again on every export and tick-side change.
const pageInkCache = new WeakMap();

async function loadPageInk(file, pdfDoc, pageInfo, control) {
    if (!pageInkCache.has(file)) pageInkCache.set(file, new Map());
    const cache = pageInkCache.get(file);
    if (cache.has(pageInfo.page)) return cache.get(pageInfo.page);
    const page = await pdfDoc.getPage(pageInfo.page);
    const scale = INK_RASTER_WIDTH / page.getViewport({ scale: 1 }).width;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, viewport }).promise;
    if (control.cancelled) return null;
    const { data, width, height } = context.getImageData(0, 0, canvas.width, canvas.height);
    const ruleMask = inkMaskFromRgba(data, width, height);
    const ink = { width, height, ruleMask, penMask: penMaskFromRgba(data, width, height),
        rules: findRuledLines(ruleMask, width, height),
        mask: clearPrintedText(inkMaskFromRgba(data, width, height), width, height, pageInfo.items) };
    canvas.width = 0;
    canvas.height = 0;
    cache.set(pageInfo.page, ink);
    return ink;
}

/** Moves AI-placed marks onto the learner's real ink. Anything uncertain keeps its position. */
async function placeAnnotationsOnInk(file, pdfDoc, annotations, printedPages, printed, control) {
    if (typeof document === 'undefined') return annotations;
    const output = [...annotations];
    const groups = new Map();
    annotations.forEach((annotation, position) => {
        if (/^(?:manual_|teacher_|pdf_text_anchor|page_review_strip)/.test(annotation.sourceCoordinate || '')) return;
        const key = questionAnchorKey(annotation.questionNumber);
        if (!key) return;
        const exact = printed.index.get(key) || [];
        const hinted = exact.filter(candidate => candidate.page === Number(annotation.page));
        const label = hinted.length === 1 ? hinted[0] : exact.length === 1 ? exact[0] : null;
        const box = getNormalizedEvidenceBox(annotation);
        const point = getTickCoordinates(annotation);
        const page = label?.page ?? Number(annotation.page);
        if (!label && !box && !point) return;
        const groupKey = `${key}|${page}`;
        if (!groups.has(groupKey)) groups.set(groupKey, { key, page, label, positions: [], pointHeights: [], top: 1, bottom: 0, box: null });
        const group = groups.get(groupKey);
        group.positions.push(position);
        group.box = group.box || box;
        if (point) group.pointHeights.push(point.y);
        // A question can span several rows, so every mark's own height widens the area.
        const heights = [point?.y, box?.y, box ? box.y + box.height : undefined].filter(Number.isFinite);
        group.top = Math.min(group.top, ...heights);
        group.bottom = Math.max(group.bottom, ...heights);
    });
    const scanGroups = [...groups.values()].filter(group => !group.label);
    const orderedRows = new Map();
    for (const group of groups.values()) {
        if (control.cancelled) return annotations;
        const pageInfo = printedPages.find(entry => entry.page === group.page);
        if (!pageInfo) continue;
        const ink = await loadPageInk(file, pdfDoc, pageInfo, control);
        if (!ink) continue;
        const inkIn = (mask, band, ruleMask) => findInkUnits(mask, ink.width, ink.height, band, ruleMask);
        let units = [];
        if (group.label) {
            const { label } = group;
            const band = { left: label.right, right: 1, top: label.cy - label.h * 0.9, bottom: label.regionEnd };
            units = inkIn(ink.penMask, band, ink.ruleMask);
            if (units.length === 0) units = inkIn(ink.mask, band);
        } else {
            // A scan has no printed text to clear, so pen colour separates writing from print. A row
            // holding another question stays with that question.
            const penOnPage = hasPenInk(ink.penMask);
            if (!orderedRows.has(group.page)) {
                const pageGroups = scanGroups.filter(other => other.page === group.page)
                    .map(other => Object.assign(other, { pointHeights: other.pointHeights.length ? other.pointHeights : [(other.top + other.bottom) / 2] }));
                orderedRows.set(group.page, assignRowsInOrder(pageGroups, enclosedRows(ink.ruleMask, ink.width, ink.height, ink.rules)));
            }
            const orderedRow = orderedRows.get(group.page).get(group);
            const area = { top: group.top, bottom: group.bottom };
            const search = orderedRow || scanSearchArea(ink.ruleMask, ink.width, ink.height, ink.rules, area);
            const isShared = band => scanGroups.some(other => other !== group && other.page === group.page
                && (other.top + other.bottom) / 2 > band.top && (other.top + other.bottom) / 2 < band.bottom);
            if (orderedRow || !isShared(search)) units = inkIn(ink.penMask, search, ink.ruleMask);
            if (units.length === 0 && penOnPage && !orderedRow) {
                // The box can sit on the question while the answer box is just below it.
                for (const row of rowsBelow(ink.ruleMask, ink.width, ink.height, ink.rules, group.bottom)) {
                    units = isShared(row) ? [] : inkIn(ink.penMask, row, ink.ruleMask);
                    if (units.length > 0) break;
                }
            }
            // Black pen or pencil cannot be told from print, so only the AI's own box is searched.
            if (units.length === 0 && !penOnPage && group.box) {
                const { box } = group;
                units = inkIn(ink.mask, { left: box.x, right: Math.min(1, box.x + box.width + 0.02), top: box.y - 0.004, bottom: box.y + box.height + 0.004 });
            }
        }
        if (units.length === 0) continue;
        const ordered = [...group.positions].sort((a, b) => Number(annotations[a].markNumber || 0) - Number(annotations[b].markNumber || 0));
        tickPositions(units, ordered.length, ink.height / ink.width).forEach((tick, index) => {
            const { x, y, tickHeight, box } = tick;
            output[ordered[index]] = { ...output[ordered[index]], x, y, tickPoint: { x, y }, tickHeight, page: group.page,
                evidenceBox: { x: box.left, y: box.top, width: box.right - box.left, height: box.bottom - box.top },
                needsPlacement: false, locationNeedsReview: false, sourceCoordinate: 'ink_anchor' };
        });
    }
    return output;
}

async function snapAnnotationsInternal(file, annotations, side, control) {
    if (!file || !isPdf(file) || !Array.isArray(annotations) || annotations.length === 0) {
        return annotations;
    }
    let pdfDoc = null;
    try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        if (control.cancelled) return annotations;
        control.task = getDocument({ data: bytes, useWorkerFetch: false, isEvalSupported: false });
        pdfDoc = await control.task.promise;
        const rowsByKey = new Map();
        const printedPages = [];
        const wanted = new Set(annotations.map(annotation => normalizeQuestionLabel(annotation.questionNumber)));
        const normalizedText = value => String(value || '').toLowerCase().replace(/[^\p{L}\p{M}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim();
        const pageCount = pdfDoc.numPages || 0;
        let hasTextLayer = false;
        for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
            if (control.cancelled) return annotations;
            const page = await pdfDoc.getPage(pageNumber);
            const viewport = page.getViewport({ scale: 1 });
            const content = await page.getTextContent();
            const items = content.items
                .filter((item) => typeof item.str === 'string' && item.str.trim() !== '')
                .map((item) => {
                    const [a, b, c, d, tx, ty] = item.transform;
                    const baseline = Math.hypot(a, b) || 1; const upright = Math.hypot(c, d) || 1;
                    const h = Math.abs(item.height) || Math.abs(item.transform[3]) || 10;
                    const points = [0, item.width || 0].flatMap(distance => [-.2, .8].map(ascent => viewport.convertToViewportPoint(
                        tx + a / baseline * distance + c / upright * h * ascent,
                        ty + b / baseline * distance + d / upright * h * ascent)));
                    const left = Math.min(...points.map(point => point[0])); const right = Math.max(...points.map(point => point[0]));
                    const top = Math.min(...points.map(point => point[1])); const bottom = Math.max(...points.map(point => point[1]));
                    return {
                        str: item.str.trim(),
                        x: left / viewport.width,
                        right: right / viewport.width,
                        cy: (top + bottom) / 2 / viewport.height,
                        h: (bottom - top) / viewport.height,
                    };
                });
            if (items.length > 0) hasTextLayer = true;
            printedPages.push({ page: pageNumber, width: viewport.width, height: viewport.height, items });
            const labels = items
                .map((item) => {
                    const match = item.str.match(/^(?:(?:question|vraag)\s*)?(\d+(?:\.\d+)+|\d+)[.:)]?(?:\s|$)/i);
                    return match && item.x < 0.3 ? { ...item, key: normalizeQuestionLabel(match[1]) } : null;
                })
                .filter(Boolean)
                .sort((a, b) => a.cy - b.cy);
            labels.forEach((label, index) => {
                if (!wanted.has(label.key)) return;
                if (!rowsByKey.has(label.key)) rowsByKey.set(label.key, []);
                const top = label.cy + label.h * 0.6;
                const next = labels[index + 1];
                const bottom = next ? next.cy - next.h : label.cy + label.h * 8;
                const rowItems = items.filter((item) => item.cy >= top && item.cy < bottom && item.x >= label.x - 0.02
                    && !/\b(?:bladsy|page)\s+\d+\s+(?:van|of)\s+\d+/i.test(item.str));
                if (rowItems.length === 0) return;
                const textLines = [];
                rowItems.sort((a, b) => a.cy - b.cy || a.x - b.x).forEach(item => {
                    let line = textLines.find(candidate => Math.abs(candidate.cy - item.cy) <= label.h * 0.6);
                    if (!line) { line = { cy: item.cy, items: [] }; textLines.push(line); }
                    line.items.push(item);
                });
                textLines.forEach(line => { line.items.sort((a, b) => a.x - b.x); line.text = line.items.map(item => item.str).join(' '); });
                const lineYs = textLines.map(line => line.cy);
                const rightEdge = Math.max(...rowItems.map((item) => item.right));
                const leftEdge = Math.min(...rowItems.map((item) => item.x));
                rowsByKey.get(label.key).push({
                    page: pageNumber,
                    lineYs,
                    rightEdge,
                    labelX: label.x,
                    textLines,
                    box: {
                        x: leftEdge,
                        y: lineYs[0] - label.h / 2,
                        width: Math.max(0.01, rightEdge - leftEdge),
                        height: lineYs[lineYs.length - 1] - lineYs[0] + label.h,
                    },
                });
            });
        }
        const printed = indexPrintedMarkAnchors(printedPages);
        const placeOnInk = list => placeAnnotationsOnInk(file, pdfDoc, list, printedPages, printed, control).catch(() => list);
        if (!hasTextLayer) {
            return await placeOnInk(annotations); // scanned paper: the AI's box sets the search area
        }
        const clamp = (v) => Math.min(0.985, Math.max(0.01, v));
        const printedSlots = new Map();
        return await placeOnInk(annotations.map((annotation) => {
            if (/^(?:manual_|teacher_)/.test(annotation.sourceCoordinate || '')) {
                return annotation;
            }
            const key = normalizeQuestionLabel(annotation.questionNumber);
            const candidates = rowsByKey.get(key) || [];
            const answer = normalizedText(annotation.evidenceText);
            const matchesAnswer = line => {
                const text = normalizedText(line.text);
                return text !== '' && answer !== '' && (` ${answer} `.includes(` ${text} `) || ` ${text} `.includes(` ${answer} `));
            };
            const matchingCandidates = candidates.map(candidate => ({ ...candidate, answerLines: candidate.textLines.filter(matchesAnswer) }))
                .filter(candidate => candidate.answerLines.length > 0);
            const matched = matchingCandidates.find(candidate => candidate.page === Number(annotation.page)) || matchingCandidates[0];
            let row = matched;
            if (row) {
                const numbered = row.answerLines.filter(line => /^\s*(?:[1-5][.):]|\([1-5]\))\s*/.test(line.text));
                const answerLines = numbered.length > 0 ? numbered : row.answerLines;
                row = { ...row, lineYs: answerLines.map(line => line.cy),
                    rightEdge: Math.max(...answerLines.flatMap(line => line.items.map(item => item.right))),
                    box: { ...row.box, y: answerLines[0].cy - 0.007,
                        height: Math.max(0.014, answerLines[answerLines.length - 1].cy - answerLines[0].cy + 0.014) } };
            }
            if (!row) {
                const coordinates = getTickCoordinates(annotation);
                const box = getNormalizedEvidenceBox(annotation);
                const page = Number(annotation.page);
                // Hybrid PDFs expose the printed questions as text but not handwritten
                // answers. A failed text search must not erase a validated visual anchor,
                // especially when this function runs again during PDF export.
                if (coordinates && box && Number.isInteger(page) && page >= 1 && page <= pageCount
                    && coordinates.x >= box.x && coordinates.x <= box.x + box.width
                    && coordinates.y >= box.y && coordinates.y <= box.y + box.height) {
                    return annotation;
                }
                // A verified Vision box may be usable even when the provider omitted a tick point.
                if (!coordinates && box && annotation.needsPlacement === false && page >= 1 && page <= pageCount) {
                    const point = { x: clamp(box.x + box.width), y: clamp(box.y + box.height / 2) };
                    return { ...annotation, ...point, tickPoint: point, sourceCoordinate: 'vision_box_anchor' };
                }
                const fallback = printedAnchorFor(annotation, printed);
                if (fallback) {
                    const slotKey = `${fallback.page}|${fallback.anchor.x}|${fallback.anchor.cy}`;
                    const markIndex = printedSlots.get(slotKey) || 0; printedSlots.set(slotKey, markIndex + 1);
                    const spacing = fallback.parentFallback ? 100 : 14;
                    // Place the glyph centre 8 pt after the bracket; extra marks
                    // extend into reserved whitespace instead of over the learner's ink.
                    let pointX = fallback.kind === 'mark_bracket'
                        ? fallback.anchor.right + (8 + markIndex * spacing) / fallback.width
                        : fallback.anchor.x - (8 + markIndex * spacing) / fallback.width;
                    // Without a mark bracket, the margin is clear of the learner's work and the row number.
                    if (fallback.kind !== 'mark_bracket' && !fallback.tableCell) pointX = 1.02 + markIndex * spacing / fallback.width;
                    const point = { x: pointX,
                    y: clamp(fallback.anchor.cy) };
                    return { ...annotation, ...point, page: fallback.page, tickPoint: point, needsPlacement: false,
                        sourceCoordinate: 'pdf_printed_anchor', printedAnchorKind: fallback.kind, printedAnchorParent: fallback.parentFallback,
                        scoreLabel: fallback.parentFallback ? `Q${annotation.questionNumber} ${annotation.scoreLabel || ''}`.trim() : annotation.scoreLabel,
                        printedPageWidth: fallback.width,
                        stampLabelOffset: fallback.parentFallback ? 13 : 13 + 14 * Math.max(0,
                            annotations.filter(item => normalizeQuestionLabel(item.questionNumber) === key).length - markIndex - 1),
                        evidenceBox: { x: fallback.anchor.x, y: fallback.anchor.cy - fallback.anchor.h / 2,
                            width: Math.max(.001, fallback.anchor.right - fallback.anchor.x), height: fallback.anchor.h },
                        locationNeedsReview: false };
                }
                return {
                    ...annotation,
                    x: null,
                    y: null,
                    tickPoint: null,
                    needsPlacement: true,
                    sourceCoordinate: 'unlocated_answer',
                };
            }
            const markIndex = Math.max(0, Number(annotation.markNumber || 1) - 1);
            const total = Math.max(
                markIndex + 1,
                ...annotations
                    .filter((a) => normalizeQuestionLabel(a.questionNumber) === key)
                    .map((a) => Number(a.markNumber || 1)),
            );
            const lines = row.lineYs.length;
            const lineIdx = total <= lines ? Math.floor((markIndex * lines) / total) : markIndex % lines;
            const column = total <= lines ? 0 : Math.floor(markIndex / lines);
            const labelPos = Number.isFinite(row.labelX) ? row.labelX : row.box.x;
            const x = side === 'left'
                ? clamp(Math.max(0.015, labelPos - 0.035 - column * 0.025))
                : clamp(row.rightEdge + 0.02 + column * 0.03);
            const y = clamp(row.lineYs[lineIdx]);
            return {
                ...annotation,
                page: row.page,
                x,
                y,
                tickPoint: { x, y },
                evidenceBox: row.box,
                needsPlacement: false,
                sourceCoordinate: 'pdf_text_anchor',
                alignmentSide: side,
            };
        }));
    } catch {
        return annotations;
    } finally {
            try {
                if (typeof pdfDoc?.destroy === 'function') await pdfDoc.destroy();
                else await control.task?.destroy?.();
            } catch { /* best-effort worker cleanup */ }
    }
}

export async function snapAnnotationsToPdfText(file, annotations, side = 'left', timeoutMs = 20000) {
    const control = { cancelled: false, task: null }; let timer;
    return Promise.race([snapAnnotationsInternal(file, annotations, side, control), new Promise(resolve => {
        timer = setTimeout(() => {
            control.cancelled = true;
            try { Promise.resolve(control.task?.destroy?.()).catch(() => {}); } catch { /* unavailable worker */ }
            resolve(annotations);
        }, timeoutMs);
    })]).finally(() => clearTimeout(timer));
}

export async function extractPdfFirstPageText(file) {
    if (!isPdf(file) || typeof file.arrayBuffer !== 'function') {
        return '';
    }
    let pdfDoc = null;
    try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        pdfDoc = await getDocument({ data: bytes, useWorkerFetch: false, isEvalSupported: false }).promise;
        if (!pdfDoc || pdfDoc.numPages < 1) return '';
        const page = await pdfDoc.getPage(1);
        const content = await page.getTextContent();
        return content.items
            .map((item) => (typeof item.str === 'string' ? item.str.trim() : ''))
            .filter(Boolean)
            .join(' ');
    } catch {
        return '';
    } finally {
        if (pdfDoc && typeof pdfDoc.destroy === 'function') {
            try { pdfDoc.destroy(); } catch { /* ignore */ }
        }
    }
}

export async function locateInlineProofreading(file, results, timeoutMs = 10000) {
    const fallback = collectInlineAnnotations(results);
    if (!fallback.length || !isPdf(file) || typeof file.arrayBuffer !== 'function') return fallback;
    let pdfDoc; let task; let timer; let cancelled = false;
    const work = (async () => {
        try {
            const bytes = new Uint8Array(await file.arrayBuffer());
            if (cancelled) return fallback;
            task = getDocument({ data: bytes, useWorkerFetch: false, isEvalSupported: false });
            pdfDoc = await task.promise;
            if (cancelled || pdfDoc.numPages > 100) return fallback;
            let context = null;
            if (typeof document !== 'undefined' && typeof CanvasRenderingContext2D !== 'undefined') context = document.createElement('canvas').getContext('2d');
            const pages = [];
            for (let number = 1; number <= pdfDoc.numPages && !cancelled; number += 1) {
                const page = await pdfDoc.getPage(number);
                const viewport = page.getViewport({ scale: 1 });
                const content = await page.getTextContent();
                const words = [];
                for (const item of content.items) {
                    if (!item.str?.trim() || !item.transform || !item.width) continue;
                    const [a, b, c, d, x, y] = item.transform;
                    const baseline = Math.hypot(a, b) || 1;
                    const upright = Math.hypot(c, d) || 1;
                    const height = Math.abs(item.height) || upright;
                    if (context) context.font = `${height}px ${content.styles?.[item.fontName]?.fontFamily || 'sans-serif'}`;
                    const measured = context?.measureText(item.str).width;
                    const ratio = index => measured > 0 ? context.measureText(item.str.slice(0, index)).width / measured : index / item.str.length;
                    for (const match of item.str.matchAll(/\S+/gu)) {
                        const from = ratio(match.index) * item.width;
                        const to = ratio(match.index + match[0].length) * item.width;
                        const points = [from, to].flatMap(distance => [-.2, .8].map(ascent => viewport.convertToViewportPoint(
                            x + a / baseline * distance + c / upright * height * ascent,
                            y + b / baseline * distance + d / upright * height * ascent)));
                        const left = Math.min(...points.map(point => point[0])) / viewport.width;
                        const top = Math.min(...points.map(point => point[1])) / viewport.height;
                        const width = Math.max(...points.map(point => point[0])) / viewport.width - left;
                        const wordHeight = Math.max(...points.map(point => point[1])) / viewport.height - top;
                        if ([left, top, width, wordHeight].every(Number.isFinite) && left >= 0 && top >= 0 && width > 0 && wordHeight > 0
                            && left + width <= 1 && top + wordHeight <= 1) words.push({ text: match[0], x: left, y: top, width, height: wordHeight });
                    }
                }
                // Keep PDF extraction order; context disambiguation rejects uncertain matches.
                pages.push({ page: number, words });
            }
            const validFallback = fallback.map(annotation => annotation.page > pdfDoc.numPages
                ? { ...annotation, page: null, rects: [], needsPlacement: true } : annotation);
            return cancelled ? validFallback : locateInlineInText(validFallback, pages);
        } catch { return fallback; }
        finally { if (pdfDoc) { try { await pdfDoc.destroy(); } catch { /* best-effort cleanup */ } } }
    })();
    return Promise.race([work, new Promise(resolve => { timer = setTimeout(() => {
        cancelled = true;
        try { Promise.resolve(task?.destroy?.()).catch(() => {}); } catch { /* worker unavailable */ }
        resolve(fallback);
    }, timeoutMs); })]).finally(() => clearTimeout(timer));
}

export async function extractPdfPageTexts(file) {
    if (!isPdf(file)) return [];
    let pdfDoc;
    try {
        pdfDoc = await getDocument({ data: new Uint8Array(await file.arrayBuffer()), useWorkerFetch: false, isEvalSupported: false }).promise;
        if (pdfDoc.numPages > 100) return [];
        const texts = [];
        for (let number = 1; number <= pdfDoc.numPages; number++) {
            const content = await (await pdfDoc.getPage(number)).getTextContent();
            texts.push(content.items.map(item => item.str || '').join(' '));
        }
        return texts;
    } catch { return []; }
    finally { if (pdfDoc) { try { await pdfDoc.destroy(); } catch { /* best-effort PDF cleanup */ } } }
}

export async function renderPdfPagesForAi(file, maxPages = 12) {
    if (!isPdf(file) || typeof file.arrayBuffer !== 'function' || typeof document === 'undefined') {
        return { pages: [], dimensions: [] };
    }
    let pdfDoc = null;
    try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        pdfDoc = await getDocument({ data: bytes, useWorkerFetch: false, isEvalSupported: false }).promise;
        if (!pdfDoc || pdfDoc.numPages < 1) {
            return { pages: [], dimensions: [] };
        }
        const total = Math.min(pdfDoc.numPages, maxPages);
        const pages = [];
        const dimensions = [];

        for (let pageNum = 1; pageNum <= total; pageNum += 1) {
            const page = await pdfDoc.getPage(pageNum);
            const baseViewport = page.getViewport({ scale: 1 });
            const maxDim = Math.max(baseViewport.width || 1, baseViewport.height || 1);
            const targetScale = Math.min(2.0, Math.max(1.0, 1200 / maxDim));
            const viewport = page.getViewport({ scale: targetScale });
            const width = Math.max(1, Math.round(viewport.width));
            const height = Math.max(1, Math.round(viewport.height));

            dimensions.push({ page: pageNum, width, height });

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext?.('2d');
            if (!context || typeof canvas.toBlob !== 'function') {
                continue;
            }
            context.filter = 'grayscale(100%)';
            await page.render({ canvasContext: context, viewport }).promise;
            const blob = await new Promise((resolve) => {
                canvas.toBlob((result) => resolve(result), 'image/jpeg', 0.80);
            });
            if (blob) {
                pages.push({
                    page: pageNum,
                    blob,
                    fileName: `learner-page-${pageNum}.jpg`,
                    width,
                    height,
                });
            }
        }

        return { pages, dimensions };
    } catch {
        return { pages: [], dimensions: [] };
    } finally {
        if (pdfDoc && typeof pdfDoc.destroy === 'function') {
            try { pdfDoc.destroy(); } catch { /* ignore */ }
        }
    }
}

const INK_RASTER_WIDTH = 1000;
const ARCHIVE_SMALL_ENOUGH_BYTES = 300 * 1024;
const ARCHIVE_LONG_SIDE_PX = 1600;
const ARCHIVE_JPEG_QUALITY = 0.62;

// Shrinks a stamped script for long-term storage by flattening each page to a JPEG. Colour is
// kept so red ticks stay red, and 1600px on A4 (~135 dpi) keeps handwriting readable. Returns
// the bytes unchanged when they are already small, or if this fails or saves nothing.
export async function compressMarkedPdf(pdfBytes) {
    if (!pdfBytes || pdfBytes.length <= ARCHIVE_SMALL_ENOUGH_BYTES || typeof document === 'undefined') return pdfBytes;
    let source = null;
    try {
        // pdf.js takes ownership of the buffer it is given, so it gets a copy.
        source = await getDocument({ data: pdfBytes.slice(), useWorkerFetch: false, isEvalSupported: false }).promise;
        const output = await PDFDocument.create();
        for (let pageNum = 1; pageNum <= source.numPages; pageNum += 1) {
            const page = await source.getPage(pageNum);
            const size = page.getViewport({ scale: 1 });
            const scale = ARCHIVE_LONG_SIDE_PX / Math.max(size.width, size.height);
            const viewport = page.getViewport({ scale });
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(viewport.width));
            canvas.height = Math.max(1, Math.round(viewport.height));
            const context = canvas.getContext('2d');
            // JPEG has no alpha: paint white first so transparent pages do not turn black.
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvas.width, canvas.height);
            await page.render({ canvasContext: context, viewport }).promise;
            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', ARCHIVE_JPEG_QUALITY));
            canvas.width = 0;
            canvas.height = 0;
            if (!blob) return pdfBytes;
            const image = await output.embedJpg(await blob.arrayBuffer());
            output.addPage([size.width, size.height]).drawImage(image, { x: 0, y: 0, width: size.width, height: size.height });
        }
        const compressed = await output.save();
        return compressed.length < pdfBytes.length ? compressed : pdfBytes;
    } catch {
        return pdfBytes;
    } finally {
        if (source && typeof source.destroy === 'function') {
            try { source.destroy(); } catch { /* ignore */ }
        }
    }
}

export async function createAnnotatedPdfBytes({
    file,
    annotations = [],
    marking = null,
    learnerName = '',
    schoolName = '',
    subjectName = '',
    assessmentName = '',
    pdfStampMode = 'cover',
}) {
    if (!file) throw new Error('No document file provided.');
    let pdf;
    if (isPdf(file)) {
        pdf = await PDFDocument.load(await file.arrayBuffer());
    } else {
        pdf = await PDFDocument.create();
        const bytes = await file.arrayBuffer();
        const isPng = file.type === 'image/png' || /\.png$/i.test(file.name);
        const img = isPng ? await pdf.embedPng(bytes) : await pdf.embedJpg(bytes);
        const page = pdf.addPage([img.width, img.height]);
        page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
    }

    const fontRegular = await pdf.embedFont(StandardFonts.Helvetica);
    const fontBold = await pdf.embedFont(StandardFonts.HelveticaBold);
    const pageCount = pdf.getPageCount();

    const safePdfText = (val) => String(val ?? '')
        .replace(/[\u2014\u2013]/g, '-')
        .replace(/[^\x20-\x7E\xA0-\xFF]/g, '');

    const fmtMark = (val) => {
        const num = Number(val || 0);
        return Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    };

    const resultsList = Array.isArray(marking?.results) ? marking.results : [];
    const rubricStamps = buildRubricStamps(resultsList, pageCount);
    const inlineAnnotations = await locateInlineProofreading(file, resultsList);
    const inlineBadges = buildInlineBadges(inlineAnnotations.filter(annotation => annotation.page <= pageCount));
    const rubricKeys = new Set(resultsList.filter(isRubricTask).map(result => normalizeQuestionLabel(result.questionNumber)));
    const originalBoxes = new Map();
    for (let index = 0; index < pageCount; index += 1) {
        const page = pdf.getPage(index);
        const box = page.getCropBox();
        originalBoxes.set(index, box);
        const count = rubricStamps.filter(stamp => stamp.page === index + 1).length;
        const hasInline = inlineBadges.some(badge => badge.page === index + 1);
        const unplaced = inlineAnnotations.filter(annotation => annotation.needsPlacement && Math.min(pageCount, annotation.pageHint) === index + 1);
        if (count || hasInline || unplaced.length) {
            // Add actual whitespace below/right of the original page. No learner
            // text, ink annotations or coordinates are shifted or painted over.
            const extra = count * 78 + (unplaced.length ? 32 + Math.min(6, unplaced.length) * 11 : 0);
            const rotation = ((page.getRotation().angle % 360) + 360) % 360;
            const reserve = hasInline ? 100 : 0;
            const bounds = { x: box.x - (rotation === 180 ? reserve : 0),
                y: box.y - extra - (rotation === 270 ? reserve : 0),
                width: box.width + (count ? 28 : 0) + ([0, 180].includes(rotation) ? reserve : 0),
                height: box.height + extra + ([90, 270].includes(rotation) ? reserve : 0) };
            page.setMediaBox(bounds.x, bounds.y, bounds.width, bounds.height);
            page.setCropBox(bounds.x, bounds.y, bounds.width, bounds.height);
        }
    }

    // Revalidate cached AI anchors on every export; manual placements are preserved.
    const regularAnnotations = ensureGradedItemAnnotations(annotations, resultsList).filter(annotation => !rubricKeys.has(normalizeQuestionLabel(annotation.questionNumber)) || /^manual_/.test(annotation.sourceCoordinate || ''));
    const resolvedAnnotations = await snapAnnotationsToPdfText(file, regularAnnotations,
        annotations.find(annotation => annotation.alignmentSide)?.alignmentSide || 'left');
    const exportAnnotations = withReviewStripFallback(resolvedAnnotations, pageCount, resultsList);
    // Extend the displayed right edge only when indicators need real whitespace.
    for (let index = 0; index < pageCount; index++) {
        const page = pdf.getPage(index); const box = originalBoxes.get(index);
        const rotation = ((page.getRotation().angle % 360) + 360) % 360;
        const viewWidth = [90, 270].includes(rotation) ? box.height : box.width;
        const right = Math.max(1, ...exportAnnotations.filter(annotation => annotation.page === index + 1)
            .map(annotation => {
                const label = safePdfText(annotation.reviewLabel || annotation.scoreLabel || '');
                const extra = annotation.sourceCoordinate === 'page_review_strip' ? Math.max(90, 19 + fontRegular.widthOfTextAtSize(label, 8))
                    : label ? (annotation.stampLabelOffset || 13) + fontRegular.widthOfTextAtSize(label, 8) + 6 : 10;
                return (getTickCoordinates(annotation)?.x || 0) + extra / viewWidth;
            }));
        const reserve = Math.max(0, (right - 1) * viewWidth);
        if (!reserve) continue;
        const current = page.getCropBox();
        const bounds = rotation === 90 ? { ...current, height: Math.max(current.height, box.y + box.height + reserve - current.y) }
            : rotation === 180 ? { ...current, x: Math.min(current.x, box.x - reserve), width: current.x + current.width - Math.min(current.x, box.x - reserve) }
                : rotation === 270 ? { ...current, y: Math.min(current.y, box.y - reserve), height: current.y + current.height - Math.min(current.y, box.y - reserve) }
                    : { ...current, width: Math.max(current.width, box.x + box.width + reserve - current.x) };
        page.setMediaBox(bounds.x, bounds.y, bounds.width, bounds.height); page.setCropBox(bounds.x, bounds.y, bounds.width, bounds.height);
    }
    exportAnnotations.forEach((annotation) => {
        const pageIndex = Number(annotation.page) - 1;
        if (pageIndex < 0 || pageIndex >= pageCount) return;
        const page = pdf.getPage(pageIndex);
        if (!page) return;
        const cropBox = originalBoxes.get(pageIndex);
        const pageSize = page.getSize();
        const pageBox = cropBox && cropBox.width > 0 && cropBox.height > 0
            ? cropBox
            : { x: 0, y: 0, width: pageSize.width, height: pageSize.height };
        const coordinates = getTickCoordinates(annotation);
        if (!coordinates) return;
        const rotation = page.getRotation().angle;
        const viewWidth = [90, 270].includes(((rotation % 360) + 360) % 360) ? pageBox.height : pageBox.width;
        const viewHeight = [90, 270].includes(((rotation % 360) + 360) % 360) ? pageBox.width : pageBox.height;
        const point = (dx, dy) => viewportToPdfPoint({ x: coordinates.x + dx / viewWidth, y: coordinates.y - dy / viewHeight }, pageBox, rotation);
        // Big, bold marks with a white edge so they stand out on grey scans.
        const size = annotation.tickHeight
            ? Math.max(9, Math.min(32, annotation.tickHeight * viewHeight * 1.1))
            : Math.max(13, Math.min(32, pageBox.width * 0.028));
        const color = annotation.type === 'lost'
            ? rgb(0.84, 0.16, 0.16)
            : rgb(0.07, 0.54, 0.27);
        const stroke = (start, end) => {
            const thickness = Math.max(2.2, size * 0.17);
            page.drawLine({ start, end, thickness: thickness + 2, color: rgb(1, 1, 1), lineCap: LineCapStyle.Round });
            page.drawLine({ start, end, thickness, color, lineCap: LineCapStyle.Round });
        };
        if (annotation.reviewLabel || annotation.scoreLabel) {
            const labelPoint = viewportToPdfPoint({ x: coordinates.x + (annotation.stampLabelOffset || 13) / viewWidth, y: coordinates.y + .004 }, pageBox, rotation);
            page.drawText(safePdfText(annotation.reviewLabel || annotation.scoreLabel), { ...labelPoint, font: fontRegular, size: 8,
                color, rotate: degrees(-rotation) });
        }
        if (annotation.sourceCoordinate === 'page_review_strip' && Math.abs(coordinates.y - .065) < .0001) {
            page.drawText('Location needs review', { ...point(-5, 18), font: fontBold, size: 7, color: rgb(.35, .35, .35), rotate: degrees(-rotation) });
        }
        if (annotation.type === 'lost') {
            stroke(point(-size * .4, -size * .4), point(size * .4, size * .4));
            stroke(point(-size * .4, size * .4), point(size * .4, -size * .4));
        } else {
            stroke(point(-size * .42, -size * .02), point(-size * .10, -size * .34));
            stroke(point(-size * .10, -size * .34), point(size * .45, size * .38));
        }
    });

    const proofColor = rgb(.78, .08, .1);
    for (const annotation of inlineAnnotations.filter(item => !item.needsPlacement && item.page <= pageCount)) {
        const page = pdf.getPage(annotation.page - 1);
        const box = originalBoxes.get(annotation.page - 1);
        for (const rect of annotation.rects) {
            const y = Math.min(.997, rect.y + rect.height + .0015);
            page.drawLine({ start: viewportToPdfPoint({ x: rect.x, y }, box, page.getRotation().angle),
                end: viewportToPdfPoint({ x: rect.x + rect.width, y }, box, page.getRotation().angle),
                thickness: .8, color: proofColor });
        }
    }
    for (const badge of inlineBadges) {
        const page = pdf.getPage(badge.page - 1);
        const box = originalBoxes.get(badge.page - 1);
        const rotation = ((page.getRotation().angle % 360) + 360) % 360;
        const unplacedCount = inlineAnnotations.filter(annotation => annotation.needsPlacement && Math.min(pageCount, annotation.pageHint) === badge.page).length;
        const footer = rubricStamps.filter(stamp => stamp.page === badge.page).length * 78 + (unplacedCount ? 32 + Math.min(6, unplacedCount) * 11 : 0);
        const viewportWidth = [90, 270].includes(rotation) ? box.height : box.width;
        const point = viewportToPdfPoint({ x: 1 + (34 + (rotation === 270 ? footer : 0)) / viewportWidth, y: badge.y }, box, rotation);
        const label = badge.codes.join(', ');
        const radians = rotation * Math.PI / 180;
        page.drawRectangle({ x: point.x - 3 * Math.cos(radians) + 3 * Math.sin(radians),
            y: point.y - 3 * Math.sin(radians) - 3 * Math.cos(radians),
            width: fontBold.widthOfTextAtSize(label, 8) + 6, height: 12, rotate: degrees(rotation),
            borderColor: proofColor, borderWidth: .6, color: rgb(1, 1, 1) });
        page.drawText(label, { x: point.x, y: point.y, size: 8, rotate: degrees(rotation), font: fontBold, color: proofColor });
    }

    for (let index = 0; index < pageCount; index += 1) {
        const unplaced = inlineAnnotations.filter(annotation => annotation.needsPlacement && Math.min(pageCount, annotation.pageHint) === index + 1);
        if (!unplaced.length) continue;
        const page = pdf.getPage(index); const box = originalBoxes.get(index);
        let y = box.y - rubricStamps.filter(stamp => stamp.page === index + 1).length * 78 - 12;
        page.drawText('Proofreading corrections - location needs teacher review', { x: box.x + 18, y, size: 8, font: fontBold, color: proofColor });
        for (const annotation of unplaced.slice(0, 6)) {
            y -= 11;
            let note = safePdfText(`${annotation.error_code}: "${annotation.exact_quote}" -> ${annotation.correction}`);
            while (note.length && fontRegular.widthOfTextAtSize(note, 7) > box.width - 36) note = note.slice(0, -1);
            page.drawText(note, { x: box.x + 18, y, size: 7, font: fontRegular, color: proofColor });
        }
        if (unplaced.length > 6) page.drawText(`${unplaced.length - 6} further corrections are listed in the app.`, { x: box.x + 18, y: y - 11, size: 7, font: fontRegular, color: proofColor });
    }

    const stampOffsets = new Map();
    for (const stamp of rubricStamps) {
        const index = stamp.page - 1;
        const page = pdf.getPage(index);
        const box = originalBoxes.get(index);
        const offset = stampOffsets.get(index) || 0;
        stampOffsets.set(index, offset + 1);
        const x = box.x + 18;
        const y = box.y - (offset + 1) * 78 + 8;
        const width = box.width - 28;
        const color = Number(stamp.result.marksAwarded) === Number(stamp.result.maxMarks) ? rgb(.12, .45, .27) : rgb(.65, .14, .12);
        page.drawRectangle({ x, y, width, height: 64, borderWidth: 1, borderColor: color, color: rgb(1, 1, 1) });
        page.drawText(safePdfText(stamp.title), { x: x + 9, y: y + 48, size: 9, font: fontBold, color });
        const scoreText = safePdfText(stamp.scoreLine);
        const scoreSize = Math.min(9, (width - 18) / Math.max(1, fontBold.widthOfTextAtSize(scoreText, 1)));
        page.drawText(scoreText, { x: x + 9, y: y + 31, size: scoreSize, font: fontBold, color });
        let deduction = safePdfText(stamp.deductionLine);
        while (deduction.length && fontRegular.widthOfTextAtSize(deduction, 7) > width - 18) deduction = deduction.slice(0, -1);
        if (deduction !== safePdfText(stamp.deductionLine)) deduction = deduction.slice(0, -3) + '...';
        page.drawText(deduction, { x: x + 9, y: y + 13, size: 7, font: fontRegular, color });
        for (const tick of stamp.ticks) {
            const tx = box.x + box.width + 14;
            const ty = box.y + box.height * (1 - tick.y);
            const tickColor = tick.lost ? rgb(.72, .15, .13) : rgb(.12, .5, .28);
            if (tick.lost) {
                page.drawLine({ start: { x: tx - 3, y: ty - 3 }, end: { x: tx + 3, y: ty + 3 }, thickness: 1.5, color: tickColor });
                page.drawLine({ start: { x: tx - 3, y: ty + 3 }, end: { x: tx + 3, y: ty - 3 }, thickness: 1.5, color: tickColor });
            } else {
                page.drawLine({ start: { x: tx - 4, y: ty }, end: { x: tx - 1, y: ty - 3 }, thickness: 1.5, color: tickColor });
                page.drawLine({ start: { x: tx - 1, y: ty - 3 }, end: { x: tx + 4, y: ty + 4 }, thickness: 1.5, color: tickColor });
            }
        }
    }

    const totals = calculateMarkingTotals(resultsList);
    const totalAwarded = resultsList.length > 0
        ? totals.totalAwarded
        : marking?.totalMarks > 0 ? Number(marking.totalAwarded || 0)
            : annotations.filter((a) => a.type !== 'lost' && a.awarded !== false).length;
    const totalMax = marking?.totalMarks > 0
        ? Number(marking.totalMarks)
        : resultsList.length > 0 ? totals.totalMarks : annotations.length;
    const pct = totalMax > 0 ? ((totalAwarded / totalMax) * 100).toFixed(1) : '0.0';

    const sectionEntries = Object.entries(totals.sectionTotals);

    if (pdfStampMode === 'cover') {
        const firstOrigPage = pdf.getPage(0);
        const pageSize = firstOrigPage.getSize();
        const coverPage = pdf.insertPage(0, [pageSize.width, pageSize.height]);
        const coverWidth = pageSize.width;
        const coverHeight = pageSize.height;
        const margin = 40;
        const contentW = coverWidth - margin * 2;

        coverPage.drawRectangle({
            x: margin,
            y: coverHeight - margin - 50,
            width: contentW,
            height: 50,
            color: rgb(0.06, 0.09, 0.16),
        });
        coverPage.drawText('MARKSURE ASSESSMENT SYSTEM', {
            x: margin + 14,
            y: coverHeight - margin - 20,
            size: 8,
            font: fontBold,
            color: rgb(0.65, 0.74, 0.86),
        });
        coverPage.drawText(resultsList.some(result => result.needsReview) ? 'AI draft - teacher review required' : 'Teacher-reviewed Script & Moderation Record', {
            x: margin + 14,
            y: coverHeight - margin - 38,
            size: 13.5,
            font: fontBold,
            color: rgb(1, 1, 1),
        });

        let curY = coverHeight - margin - 68;

        const effectiveLearner = safePdfText(learnerName || marking?.learnerName || marking?.learner || '-');
        const effectiveSchool = safePdfText(schoolName || marking?.school || '-');
        const effectiveSubject = safePdfText(subjectName || marking?.subjectName || marking?.subject || '-');
        const effectiveAssessment = safePdfText((assessmentName || 'Assessment').replace(/\(out of\s+[\d.]+\)/gi, `(out of ${fmtMark(totalMax)})`));

        coverPage.drawRectangle({
            x: margin,
            y: curY - 58,
            width: contentW,
            height: 58,
            color: rgb(0.97, 0.98, 0.99),
            borderColor: rgb(0.82, 0.86, 0.91),
            borderWidth: 0.8,
        });
        coverPage.drawText(`Learner: ${effectiveLearner}`, {
            x: margin + 12,
            y: curY - 18,
            size: 9.5,
            font: fontBold,
            color: rgb(0.06, 0.09, 0.16),
        });
        coverPage.drawText(`School: ${effectiveSchool}`, {
            x: margin + contentW / 2 + 10,
            y: curY - 18,
            size: 9,
            font: fontRegular,
            color: rgb(0.28, 0.33, 0.41),
        });
        coverPage.drawText(`Assessment: ${effectiveAssessment}`, {
            x: margin + 12,
            y: curY - 34,
            size: 9,
            font: fontRegular,
            color: rgb(0.28, 0.33, 0.41),
        });
        coverPage.drawText(`Subject: ${effectiveSubject}`, {
            x: margin + contentW / 2 + 10,
            y: curY - 34,
            size: 9,
            font: fontRegular,
            color: rgb(0.28, 0.33, 0.41),
        });
        coverPage.drawText(`Date marked: ${new Date().toLocaleDateString()}`, {
            x: margin + 12,
            y: curY - 50,
            size: 8.5,
            font: fontRegular,
            color: rgb(0.4, 0.45, 0.53),
        });

        curY -= 74;

        // Section totals wrap onto as many lines as they need (up to 4) inside the result box.
        const secEntries = sectionEntries.map(([sec, s]) => safePdfText(
            `Sec ${sec}: ${fmtMark(s.awarded)}/${fmtMark(s.max)} (${s.max > 0 ? ((s.awarded / s.max) * 100).toFixed(0) : 0}%)`));
        const secLines = [];
        for (const entry of secEntries) {
            const last = secLines[secLines.length - 1];
            const joined = last ? `${last}   •   ${entry}` : entry;
            if (last && fontRegular.widthOfTextAtSize(joined, 8.5) > contentW - 28) secLines.push(entry);
            else if (last) secLines[secLines.length - 1] = joined;
            else secLines.push(entry);
        }
        if (secLines.length > 4) {
            secLines.length = 4;
            secLines[3] = `${secLines[3]}   •   …`;
        }
        const resultH = 46 + secLines.length * 12;

        coverPage.drawRectangle({
            x: margin,
            y: curY - resultH,
            width: contentW,
            height: resultH,
            color: rgb(0.94, 0.99, 0.96),
            borderColor: rgb(0.12, 0.54, 0.33),
            borderWidth: 1.2,
        });
        coverPage.drawText('ASSESSMENT RESULT', {
            x: margin + 14,
            y: curY - 17,
            size: 8,
            font: fontBold,
            color: rgb(0.12, 0.45, 0.33),
        });
        coverPage.drawText(`TOTAL SCORE: ${fmtMark(totalAwarded)} / ${fmtMark(totalMax)}  (${pct}%)`, {
            x: margin + 14,
            y: curY - 38,
            size: 14.5,
            font: fontBold,
            color: rgb(0.09, 0.45, 0.27),
        });

        secLines.forEach((line, index) => {
            coverPage.drawText(line, {
                x: margin + 14,
                y: curY - 52 - index * 12,
                size: 8.5,
                font: fontRegular,
                color: rgb(0.2, 0.35, 0.28),
            });
        });

        curY -= resultH + 16;

        if (marking?.summaryText) {
            coverPage.drawRectangle({
                x: margin,
                y: curY - 48,
                width: contentW,
                height: 48,
                color: rgb(0.95, 0.96, 0.98),
                borderColor: rgb(0.85, 0.88, 0.92),
                borderWidth: 0.8,
            });
            coverPage.drawText('DIAGNOSTIC STRUGGLE SUMMARY', {
                x: margin + 12,
                y: curY - 16,
                size: 8,
                font: fontBold,
                color: rgb(0.2, 0.25, 0.33),
            });
            coverPage.drawText(safePdfText(marking.summaryText).slice(0, 110), {
                x: margin + 12,
                y: curY - 32,
                size: 8.5,
                font: fontRegular,
                color: rgb(0.12, 0.16, 0.23),
            });
            curY -= 62;
        }

        coverPage.drawRectangle({
            x: margin,
            y: curY - 42,
            width: contentW,
            height: 42,
            color: rgb(1, 1, 1),
            borderColor: rgb(0.82, 0.86, 0.91),
            borderWidth: 0.8,
        });
        coverPage.drawText('Examiner Signature: ______________________      Date: ____________      HOD / Moderator: ______________________', {
            x: margin + 10,
            y: curY - 25,
            size: 8,
            font: fontRegular,
            color: rgb(0.35, 0.4, 0.48),
        });

        curY -= 52;

        coverPage.drawText('* Note: The original learner script with examiner ticks follows on Page 2 with 100% of student details preserved.', {
            x: margin,
            y: curY - 10,
            size: 7.8,
            font: fontRegular,
            color: rgb(0.45, 0.5, 0.58),
        });
    } else if (pdfStampMode === 'margin' && pageCount > 0) {
        const firstOrigPage = pdf.getPage(0);
        const cropBox = typeof firstOrigPage.getCropBox === 'function' ? firstOrigPage.getCropBox() : null;
        const pageSize = firstOrigPage.getSize();
        const pageBox = cropBox && cropBox.width > 0 && cropBox.height > 0
            ? cropBox
            : { x: 0, y: 0, width: pageSize.width, height: pageSize.height };

        const topBannerText = safePdfText(`MarkSure Score: ${fmtMark(totalAwarded)} / ${fmtMark(totalMax)} (${pct}%)  •  ${learnerName || ''}`);
        firstOrigPage.drawText(topBannerText, {
            x: pageBox.x + 18,
            y: pageBox.y + pageBox.height - 12,
            size: 8.5,
            font: fontBold,
            color: rgb(0.09, 0.45, 0.27),
        });
    }

    return await pdf.save();
}

export default function MarkingPaperAnnotations({
    file,
    annotations = EMPTY_ANNOTATIONS,
    marking = null,
    learnerName = '',
    schoolName = '',
    subjectName = '',
    assessmentName = '',
    onChange,
    activeQuestionNumber = null,
    onSelectQuestion = null,
    targetPage = null,
    readOnly = false,
}) {
    const [active, setActive] = useState(false);
    const [placingAnnotationIndex, setPlacingAnnotationIndex] = useState(null);
    const [selectedTickIndex, setSelectedTickIndex] = useState(null);
    const [draggingTickIndex, setDraggingTickIndex] = useState(null);
    const [dragMoved, setDragMoved] = useState(false);
    const [objectUrl, setObjectUrl] = useState('');
    const [pdfDocument, setPdfDocument] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [tickSide, setTickSide] = useState('left');
    const [pdfState, setPdfState] = useState({ loading: false, error: '' });
    const [downloadState, setDownloadState] = useState({ loading: false, error: '' });
    const [inlineProofreading, setInlineProofreading] = useState([]);
    const [resolvedTickState, setResolvedTickState] = useState(null);
    const [pdfStampMode, setPdfStampMode] = useState('cover'); // 'cover' | 'margin' | 'ticks_only'
    const canvasRef = useRef(null);
    const pdfViewerRef = useRef(null);
    const svgRef = useRef(null);
    const prevActiveQuestionRef = useRef(null);
    const prevTargetPageRef = useRef(targetPage);

    const pdfFile = isPdf(file);

    useEffect(() => {
        if (selectedTickIndex === null) return undefined;
        const handleKeyDown = (event) => {
            const tag = event.target?.tagName?.toLowerCase() || '';
            if (tag === 'input' || tag === 'textarea' || tag === 'select' || event.target?.isContentEditable) {
                return;
            }
            if (event.key === 'Delete' || event.key === 'Backspace') {
                event.preventDefault();
                setPlacingAnnotationIndex((curr) => (curr === selectedTickIndex ? null : curr));
                setDraggingTickIndex((curr) => (curr === selectedTickIndex ? null : curr));
                const idxToRemove = selectedTickIndex;
                setSelectedTickIndex(null);
                onChange?.((Array.isArray(annotations) ? annotations : []).filter((_, idx) => idx !== idxToRemove));
            } else if (event.key === 'Escape') {
                setSelectedTickIndex(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedTickIndex, annotations, onChange]);

    useEffect(() => {
        if (!file) {
            setObjectUrl('');
            return undefined;
        }
        const url = URL.createObjectURL(file);
        setObjectUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    useEffect(() => {
        if (!pdfFile || !file) {
            setPdfDocument(null);
            setCurrentPage(1);
            setPdfState({ loading: false, error: '' });
            return undefined;
        }

        let cancelled = false;
        setPdfState({ loading: true, error: '' });
        setPdfDocument(null);
        setCurrentPage(1);

        file.arrayBuffer()
            .then((data) => {
                const bytes = new Uint8Array(data);
                const header = new TextDecoder().decode(bytes.slice(0, 5));
                if (header !== '%PDF-') {
                    throw new Error(`The uploaded file does not start with a PDF header (${header || 'empty file'}).`);
                }
                return getDocument({
                    data: bytes,
                    useWorkerFetch: false,
                    isEvalSupported: false,
                }).promise;
            })
            .then((document) => {
                if (cancelled) {
                    if (document && typeof document.destroy === 'function') {
                        try {
                            document.destroy();
                        } catch {
                            // ignore cleanup error
                        }
                    }
                    return;
                }
                setPdfDocument(document);
                setPdfState({ loading: false, error: '' });
            })
            .catch((error) => {
                if (!cancelled) {
                    const detail = error?.message ? ` (${error.message})` : '';
                    setPdfState({
                        loading: false,
                        error: `This PDF could not be displayed${detail} Download it to check that the file is not damaged.`,
                    });
                }
            });

        return () => {
            cancelled = true;
        };
    }, [file, pdfFile]);

    useEffect(() => () => {
        if (pdfDocument && typeof pdfDocument.destroy === 'function') {
            try {
                pdfDocument.destroy();
            } catch {
                // ignore cleanup error
            }
        }
    }, [pdfDocument]);

    useEffect(() => {
        if (!pdfDocument || !canvasRef.current) return undefined;

        let cancelled = false;
        const renderPage = async () => {
            try {
                const page = await pdfDocument.getPage(currentPage);
                if (cancelled || !canvasRef.current) return;
                const containerWidth = pdfViewerRef.current?.clientWidth || 900;
                const baseViewport = page.getViewport({ scale: 1 });
                const scale = Math.max(0.5, Math.min(2, containerWidth / baseViewport.width));
                const viewport = page.getViewport({ scale });
                const canvas = canvasRef.current;
                const width = Math.round(viewport.width);
                const height = Math.round(viewport.height);
                canvas.width = width;
                canvas.height = height;
                canvas.style.width = `${width}px`;
                canvas.style.height = 'auto';
                await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
            } catch {
                if (!cancelled) {
                    setPdfState((state) => ({ ...state, error: 'The PDF page could not be rendered.' }));
                }
            }
        };
        renderPage();
        return () => {
            cancelled = true;
        };
    }, [pdfDocument, currentPage]);

    const seededAnnotations = useMemo(() => (
        Array.isArray(annotations) ? ensureGradedItemAnnotations(annotations, marking?.results || []).filter((item) => item && typeof item === 'object'
            && (!(marking?.results || []).some(result => isRubricTask(result) && normalizeQuestionLabel(result.questionNumber) === normalizeQuestionLabel(item.questionNumber))
                || /^manual_/.test(item.sourceCoordinate || ''))) : []
    ), [annotations, marking]);
    const normalizedList = useMemo(() => {
        const list = resolvedTickState?.file === file && resolvedTickState?.seed === seededAnnotations ? resolvedTickState.annotations : seededAnnotations;
        return withReviewStripFallback(list, pdfDocument?.numPages || Math.max(1, ...list.map(item => Number(item.page) || 1)), marking?.results || []);
    }, [resolvedTickState, file, seededAnnotations, pdfDocument, marking]);
    useEffect(() => {
        const page = questionPaperPage(normalizedList, activeQuestionNumber, pdfDocument?.numPages);
        if (!page || !activeQuestionNumber) return;
        const previous = prevActiveQuestionRef.current;
        if (previous?.file !== file || previous?.question !== activeQuestionNumber || previous?.page !== page) setCurrentPage(page);
        prevActiveQuestionRef.current = { file, question: activeQuestionNumber, page };
    }, [activeQuestionNumber, normalizedList, pdfDocument, file]);
    useEffect(() => {
        if (targetPage == null || targetPage === prevTargetPageRef.current) return;
        const page = typeof targetPage === 'object' && targetPage.questionNumber
            ? questionPaperPage(normalizedList, targetPage.questionNumber, pdfDocument?.numPages)
            : Number(typeof targetPage === 'object' ? targetPage.page : targetPage);
        if (page >= 1 && (!pdfDocument || page <= pdfDocument.numPages)) {
            setCurrentPage(page); prevTargetPageRef.current = targetPage;
        }
    }, [targetPage, normalizedList, pdfDocument]);
    useEffect(() => {
        let active = true;
        if (!isPdf(file) || !marking?.results?.length) return () => { active = false; };
        snapAnnotationsToPdfText(file, seededAnnotations, tickSide).then(list => {
            if (active) setResolvedTickState({ file, seed: seededAnnotations, annotations: list });
        });
        return () => { active = false; };
    }, [file, seededAnnotations, marking, tickSide]);
    const rubricStamps = useMemo(() => buildRubricStamps(marking?.results || [], pdfDocument?.numPages || Infinity), [marking, pdfDocument]);
    useEffect(() => {
        let active = true;
        const initial = collectInlineAnnotations(marking?.results || []);
        setInlineProofreading(initial);
        if (!initial.length || !isPdf(file)) return () => { active = false; };
        locateInlineProofreading(file, marking?.results || []).then(locations => { if (active) setInlineProofreading(locations); });
        return () => { active = false; };
    }, [file, marking]);

    const placedAnnotations = useMemo(() => (
        normalizedList.filter((item) => (
            !item.needsPlacement
            && getTickCoordinates(item) !== null
            && Number.isFinite(Number(item.page))
            && Number(item.page) >= 1
        ))
    ), [normalizedList]);

    const unplacedAnnotations = useMemo(() => (
        normalizedList
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => item.needsPlacement || getTickCoordinates(item) === null)
    ), [normalizedList]);

    if (!file || !objectUrl) {
        return null;
    }

    const visibleAnnotations = placedAnnotations.filter((annotation) => (
        !pdfFile || Number(annotation.page) === currentPage
    ));
    const visibleRubricStamps = rubricStamps.filter(stamp => !pdfFile || stamp.page === currentPage);
    const visibleProofreading = inlineProofreading.filter(annotation => !annotation.needsPlacement && (!pdfFile || annotation.page === currentPage));
    const proofreadingBadges = buildInlineBadges(visibleProofreading);
    // Ticks may sit just outside the page edge. Reserve room for them as a share of the
    // page's own width (it shrinks as padding grows), so the page stays readable.
    const marginOverflow = Math.max(0, ...visibleAnnotations.map(annotation => (getTickCoordinates(annotation)?.x || 0) +
        (annotation.reviewLabel || annotation.scoreLabel ? Math.max(.16, (annotation.stampLabelOffset || 13) / (annotation.printedPageWidth || 600) + String(annotation.reviewLabel || annotation.scoreLabel).length * .008 + .012) : .02) - 1));
    const basePadding = proofreadingBadges.length ? 120 : visibleRubricStamps.length ? 32 : 16;
    const viewerRightPadding = marginOverflow > 0
        ? `max(${basePadding}px, ${Math.min(45, (marginOverflow / (1 + marginOverflow)) * 100).toFixed(1)}%)`
        : basePadding;

    const suggestionPages = pdfFile && pdfDocument
        ? Array.from(new Set(placedAnnotations
            .filter((annotation) => annotation.type === 'suggested')
            .map((annotation) => Number(annotation.page))
            .filter((page) => page >= 1 && page <= pdfDocument.numPages)))
            .sort((left, right) => left - right)
        : [];

    const addOrPlaceTick = (event) => {
        if (readOnly) return;
        if (dragMoved) {
            setDragMoved(false);
            return;
        }
        if (selectedTickIndex !== null && placingAnnotationIndex === null) {
            setSelectedTickIndex(null);
            if (!active) return;
        }
        if (!active && placingAnnotationIndex === null) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        const x = Number(Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width)).toFixed(5));
        const y = Number(Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height)).toFixed(5));
        const targetPage = pdfFile ? currentPage : 1;

        if (placingAnnotationIndex !== null && normalizedList[placingAnnotationIndex]) {
            const target = normalizedList[placingAnnotationIndex];
            const isLost = target.awarded === false || target.status === 'not_awarded' || target.type === 'lost';
            const updated = normalizedList.map((annotation, idx) => (
                idx === placingAnnotationIndex
                    ? {
                        ...annotation,
                        page: targetPage,
                        x,
                        y,
                        tickPoint: { x, y },
                        needsPlacement: false,
                        type: isLost ? 'lost' : 'confirmed',
                        source: 'manual',
                        sourceCoordinate: 'manual_teacher',
                    }
                    : annotation
            ));
            setPlacingAnnotationIndex(null);
            onChange?.(updated);
            return;
        }

        const nextIndex = normalizedList.length;
        onChange?.([
            ...normalizedList,
            {
                page: targetPage,
                x,
                y,
                tickPoint: { x, y },
                type: 'manual',
                status: 'awarded',
                awarded: true,
                source: 'manual',
                sourceCoordinate: 'manual_teacher',
                needsPlacement: false,
            },
        ]);
        setSelectedTickIndex(nextIndex);
    };

    const confirmSuggestion = (index) => {
        onChange?.(normalizedList.map((annotation, annotationIndex) => (
            annotationIndex === index ? { ...annotation, type: 'confirmed' } : annotation
        )));
    };

    const toggleTickStatus = (index) => {
        onChange?.(normalizedList.map((annotation, annotationIndex) => {
            if (annotationIndex !== index) return annotation;
            const currentlyLost = annotation.type === 'lost' || annotation.status === 'not_awarded' || annotation.awarded === false;
            return {
                ...annotation,
                awarded: currentlyLost,
                status: currentlyLost ? 'awarded' : 'not_awarded',
                type: currentlyLost ? 'confirmed' : 'lost',
            };
        }));
    };

    const rejectSuggestion = (index) => {
        if (placingAnnotationIndex === index) {
            setPlacingAnnotationIndex(null);
        }
        if (selectedTickIndex === index) {
            setSelectedTickIndex(null);
        }
        if (draggingTickIndex === index) {
            setDraggingTickIndex(null);
        }
        onChange?.(normalizedList.filter((_, annotationIndex) => annotationIndex !== index));
    };

    const handleSvgMouseMove = (event) => {
        if (draggingTickIndex === null || !svgRef.current) return;
        const bounds = svgRef.current.getBoundingClientRect();
        if (!bounds.width || !bounds.height) return;
        const x = Number(Math.min(0.99, Math.max(0.01, (event.clientX - bounds.left) / bounds.width)).toFixed(5));
        const y = Number(Math.min(0.99, Math.max(0.01, (event.clientY - bounds.top) / bounds.height)).toFixed(5));
        setDragMoved(true);
        onChange?.(normalizedList.map((annotation, idx) => (
            idx === draggingTickIndex
                ? {
                    ...annotation,
                    x,
                    y,
                    tickPoint: { x, y },
                    sourceCoordinate: 'manual_drag',
                }
                : annotation
        )));
    };

    const handleSvgMouseUp = () => {
        if (draggingTickIndex !== null) {
            setDraggingTickIndex(null);
        }
    };

    const pendingSuggestions = placedAnnotations.filter((annotation) => annotation.type === 'suggested').length;

    const downloadAnnotatedPdf = async () => {
        if (!pdfFile || (placedAnnotations.length === 0 && rubricStamps.length === 0 && inlineProofreading.length === 0)) return;
        setDownloadState({ loading: true, error: '' });
        try {
            const pdfBytes = await createAnnotatedPdfBytes({
                file,
                annotations: placedAnnotations,
                marking,
                learnerName,
                schoolName: schoolName || marking?.school || '',
                subjectName,
                assessmentName,
                pdfStampMode,
            });
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${file.name.replace(/\.pdf$/i, '')}-marked.pdf`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            setDownloadState({ loading: false, error: error?.message || 'The marked PDF could not be created.' });
            return;
        }
        setDownloadState({ loading: false, error: '' });
    };

    const normActive = normalizeQuestionLabel(activeQuestionNumber);

    const renderOverlaySvg = () => (
        <svg
            ref={svgRef}
            className="marking-paper-overlay"
            viewBox="0 0 1 1"
            preserveAspectRatio="none"
            style={{ ...(visibleRubricStamps.length || proofreadingBadges.length || visibleAnnotations.some(item => getTickCoordinates(item)?.x > 1) ? { overflow: 'visible' } : {}), ...(readOnly ? { pointerEvents: 'none' } : {}) }}
            aria-hidden="true"
            onClick={addOrPlaceTick}
            onMouseMove={handleSvgMouseMove}
            onMouseUp={handleSvgMouseUp}
            onMouseLeave={handleSvgMouseUp}
        >
            {visibleProofreading.map(annotation => (
                <g key={`proofread-${annotation.id}`} className="proofreading-underline" pointerEvents="none">
                    <title>{`${annotation.error_code}: ${annotation.exact_quote} → ${annotation.correction}`}</title>
                    {annotation.rects.map((rect, index) => <line key={index} x1={rect.x} x2={rect.x + rect.width}
                        y1={rect.y + rect.height + .0015} y2={rect.y + rect.height + .0015}
                        stroke="#c71424" strokeWidth="1" vectorEffect="non-scaling-stroke" />)}
                </g>
            ))}
            {proofreadingBadges.map((badge, index) => (
                <g key={`proofreading-badge-${index}`} className="proofreading-code-badge" onClick={event => event.stopPropagation()}>
                    <title>{badge.annotations.map(annotation => `${annotation.error_code}: ${annotation.exact_quote} → ${annotation.correction}`).join('\n')}</title>
                    <rect x={1.052} y={badge.y - .012} width={badge.codes.join(', ').length * .0075 + .014} height={.024}
                        fill="white" stroke="#c71424" strokeWidth=".0009" rx=".002" />
                    <text x={1.059} y={badge.y} fill="#c71424" fontSize=".014" fontWeight="700" dominantBaseline="central">{badge.codes.join(', ')}</text>
                </g>
            ))}
            {visibleRubricStamps.flatMap((stamp, stampIndex) => stamp.ticks.map((tick, tickIndex) => (
                <text key={`rubric-margin-${stampIndex}-${tickIndex}`} x={tick.x} y={tick.y}
                    textAnchor="middle" dominantBaseline="central" pointerEvents="none"
                    className={`marking-paper-tick ${tick.lost ? 'lost' : 'confirmed'}`}>
                    {tick.lost ? '×' : '✓'}
                </text>
            )))}
            {visibleAnnotations.map((annotation, index) => {
                const coords = getTickCoordinates(annotation);
                if (!coords) return null;
                const absoluteIndex = normalizedList.indexOf(annotation);
                const isItemActive = Boolean(normActive && normalizeQuestionLabel(annotation.questionNumber) === normActive);
                const isTickSelected = selectedTickIndex === absoluteIndex;
                return (
                    <g key={`tick-item-${annotation.page}-${annotation.questionNumber || 'm'}-${annotation.markNumber || index}-${index}`} className={isItemActive ? 'is-active-annotation' : ''}>
                        <title>{annotation.locationNeedsReview ? `Question ${annotation.questionNumber}: location needs review` : `Question ${annotation.questionNumber}`}</title>
                        {(annotation.reviewLabel || annotation.scoreLabel) && <text x={coords.x + (annotation.stampLabelOffset || 13) / (annotation.printedPageWidth || 600)} y={coords.y} fontSize=".013" dominantBaseline="central"
                            fill={annotation.type === 'lost' ? '#c43b36' : '#1f8a55'} pointerEvents="none">{annotation.reviewLabel || annotation.scoreLabel}</text>}
                        {annotation.sourceCoordinate === 'page_review_strip' && Math.abs(coords.y - .065) < .0001 &&
                            <text x={coords.x - .01} y={.035} fontSize=".012" fill="#555" pointerEvents="none">Location needs review</text>}
                        {(isItemActive || isTickSelected) && (
                            <circle
                                cx={coords.x}
                                cy={coords.y}
                                r={0.032}
                                className={`marking-active-tick-halo ${isTickSelected ? 'is-selected-halo' : ''}`}
                            />
                        )}
                        <text
                            x={coords.x}
                            y={coords.y}
                            textAnchor="middle"
                            dominantBaseline="central"
                            className={`marking-paper-tick ${annotation.type === 'suggested' ? 'suggested' : annotation.type === 'lost' ? 'lost' : 'confirmed'} ${isItemActive ? 'is-active-tick' : ''} ${isTickSelected ? 'is-selected-tick' : ''}`}
                            style={annotation.tickHeight ? { fontSize: `${annotation.tickHeight * 1.25}px` } : /^(?:pdf_printed_anchor|page_review_strip)$/.test(annotation.sourceCoordinate || '') ? { fontSize: '.03px' } : undefined}
                            onMouseDown={(event) => {
                                if (event.button !== 0) return;
                                event.stopPropagation();
                                if (absoluteIndex >= 0) {
                                    setSelectedTickIndex(absoluteIndex);
                                    setDraggingTickIndex(absoluteIndex);
                                    setDragMoved(false);
                                }
                            }}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (dragMoved) {
                                    setDragMoved(false);
                                    return;
                                }
                                if (absoluteIndex >= 0) {
                                    setSelectedTickIndex(absoluteIndex);
                                }
                                if (annotation.questionNumber) {
                                    onSelectQuestion?.(annotation.questionNumber);
                                }
                                if (annotation.type === 'suggested' && absoluteIndex >= 0) {
                                    confirmSuggestion(absoluteIndex);
                                }
                            }}
                            onDoubleClick={(event) => {
                                event.stopPropagation();
                                if (absoluteIndex >= 0) {
                                    toggleTickStatus(absoluteIndex);
                                }
                            }}
                            onContextMenu={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                if (absoluteIndex >= 0) {
                                    rejectSuggestion(absoluteIndex);
                                }
                            }}
                        >
                            {annotation.type === 'lost' ? '×' : '✓'}
                        </text>
                    </g>
                );
            })}
        </svg>
    );

    const selectedAnnotation = selectedTickIndex !== null ? normalizedList[selectedTickIndex] : null;

    return (
        <section className="marking-paper-card" aria-label="Teacher paper annotations">
            <div className="marking-paper-toolbar">
                <div className="paper-toolbar-left">
                    <span className="paper-toolbar-tag">Exam script</span>
                    {pdfFile && pdfDocument && (
                        <div className="paper-page-controls" aria-label="PDF page navigation">
                            <button
                                type="button"
                                className="btn-page-nav"
                                disabled={currentPage <= 1}
                                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                                title="Previous page"
                            >
                                Previous page
                            </button>
                            <span className="page-indicator">
                                Page {currentPage} of {pdfDocument.numPages}
                            </span>
                            <button
                                type="button"
                                className="btn-page-nav"
                                disabled={currentPage >= pdfDocument.numPages}
                                onClick={() => setCurrentPage((page) => Math.min(pdfDocument.numPages, page + 1))}
                                title="Next page"
                            >
                                Next page
                            </button>
                        </div>
                    )}
                    {suggestionPages.length > 0 && (
                        <div className="paper-suggestion-pills">
                            <span className="suggestion-label">Suggestions:</span>
                            {suggestionPages.map((page) => (
                                <button
                                    key={`sugg-page-${page}`}
                                    type="button"
                                    className={`btn-sugg-page ${page === currentPage ? 'selected' : ''}`}
                                    onClick={() => setCurrentPage(page)}
                                    title={`Go to page ${page}`}
                                >
                                    P{page}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {!readOnly && <div className="paper-toolbar-actions">
                    {selectedAnnotation && (
                        <div className="selected-tick-controls" role="group" aria-label="Selected tick actions">
                            <span className="selected-tick-label">
                                {selectedAnnotation.questionNumber
                                    ? `Q${selectedAnnotation.questionNumber} #${selectedAnnotation.markNumber || 1}`
                                    : 'Selected tick'}
                            </span>
                            <button
                                type="button"
                                onClick={() => toggleTickStatus(selectedTickIndex)}
                                title="Switch between awarded checkmark and unawarded cross (or double-click tick)"
                            >
                                {selectedAnnotation.type === 'lost' ? 'Change to ✓' : 'Change to ×'}
                            </button>
                            <button
                                type="button"
                                className="btn-delete-tick"
                                onClick={() => rejectSuggestion(selectedTickIndex)}
                                title="Remove this tick (or right-click tick)"
                            >
                                Delete tick
                            </button>
                        </div>
                    )}
                    <button
                        type="button"
                        className={active ? 'selected' : ''}
                        onClick={() => setActive((current) => !current)}
                        title="When on, clicking empty paper adds a new tick. Dragging existing ticks always works."
                    >
                        {active ? 'Tick tool: On' : 'Tick tool: Off'}
                    </button>
                    {pendingSuggestions > 0 && (
                        <button
                            type="button"
                            className="selected"
                            onClick={() => {
                                onChange?.(normalizedList.map((ann) => (
                                    ann.type === 'suggested' ? { ...ann, type: 'confirmed' } : ann
                                )));
                            }}
                            title="Confirm all AI-suggested ticks on the paper"
                        >
                            Confirm all ticks ({pendingSuggestions})
                        </button>
                    )}
                    {pdfFile && (
                        <button
                            type="button"
                            onClick={downloadAnnotatedPdf}
                            disabled={downloadState.loading || (placedAnnotations.length === 0 && rubricStamps.length === 0 && inlineProofreading.length === 0)}
                        >
                            {downloadState.loading ? 'Creating PDF…' : 'Download marked PDF'}
                        </button>
                    )}
                    {/* Less frequent tick settings stay one click away. Class-wide downloads are in the header's More menu. */}
                    <details className="paper-tick-options">
                        <summary>Tick options</summary>
                        <div className="paper-tick-options-list">
                    {pdfFile && (
                        <button
                            type="button"
                            onClick={async () => {
                                const nextSide = tickSide === 'left' ? 'right' : 'left';
                                setTickSide(nextSide);
                                if (file && normalizedList.length > 0) {
                                    const reAligned = await snapAnnotationsToPdfText(file, normalizedList, nextSide);
                                    onChange?.(reAligned);
                                }
                            }}
                            disabled={normalizedList.length === 0}
                            title="Switch tick positions between left margin and right margin"
                        >
                            {tickSide === 'left' ? 'Ticks: Left' : 'Ticks: Right'}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => onChange?.([])}
                        disabled={normalizedList.length === 0}
                    >
                        Clear ticks
                    </button>
                    {pdfFile && (
                        <label className="paper-stamp-mode">
                            Score on marked PDF
                            <select
                                className="select-stamp-mode"
                                value={pdfStampMode}
                                onChange={(e) => setPdfStampMode(e.target.value)}
                                title="Choose how the final score is presented in the marked PDF"
                            >
                                <option value="cover">Score on Cover Page (Preserves student info)</option>
                                <option value="margin">Score in Top Margin (Slim single line)</option>
                                <option value="ticks_only">Ticks Only (No score stamp)</option>
                            </select>
                        </label>
                    )}
                        </div>
                    </details>
                </div>}
            </div>
            <div ref={pdfViewerRef} style={{ paddingRight: viewerRightPadding }} className={`marking-paper-viewer ${pdfFile ? 'paper-pdf' : 'paper-image'}${(active || placingAnnotationIndex !== null) ? ' can-annotate' : ''}`}>
                {pdfFile ? (
                    pdfState.loading ? <p className="marking-paper-loading">Loading PDF…</p>
                        : pdfState.error ? <p className="marking-error">{pdfState.error}</p>
                            : <div className="marking-pdf-page">
                                <canvas ref={canvasRef} aria-label={`Learner exam paper page ${currentPage}`} />
                                {renderOverlaySvg()}
                            </div>
                ) : (
                    <div className="marking-pdf-page">
                        <img src={objectUrl} alt="Learner exam paper" />
                        {renderOverlaySvg()}
                    </div>
                )}
            </div>
            {visibleRubricStamps.map((stamp, index) => (
                <section key={`rubric-stamp-${index}`} className="examiner-rubric-stamp" aria-label="Examiner rubric stamp">
                    <strong>{stamp.title}</strong><p>{stamp.scoreLine}</p><small>{stamp.deductionLine}</small>
                </section>
            ))}
            {inlineProofreading.length > 0 && <section className="proofreading-review" aria-label="Red-pen proofreading corrections">
                <strong>Proofreading corrections ({inlineProofreading.length})</strong>
                <p>Suggested corrections shown here are not a verified total of all language errors.</p>
                <p>sp spelling · p punctuation · t tense · g grammar · reg register · wo word order</p>
                <ul>{inlineProofreading.map(annotation => <li key={annotation.id}>
                    <b>{annotation.error_code}</b> <q>{annotation.exact_quote}</q> → {annotation.correction}
                    <small>{annotation.needsPlacement ? 'Location needs teacher review; no guessed mark was drawn.' : `Page ${annotation.page}`}</small>
                </li>)}</ul>
            </section>}
            {unplacedAnnotations.length > 0 && (
                <div className="marking-unplaced-banner" role="region" aria-label="Marks needing teacher placement">
                    <div className="unplaced-banner-header">
                        <strong>Needs teacher placement ({unplacedAnnotations.length})</strong>
                        <button
                            type="button"
                            className="btn-auto-place-margin"
                            onClick={() => {
                                const updated = normalizedList.map((ann, idx) => {
                                    if (ann.needsPlacement || getTickCoordinates(ann) === null) {
                                        const markNum = Number(ann.markNumber || 1);
                                        const y = Math.min(0.95, Math.max(0.08, 0.10 + ((idx % 14) * 0.055) + ((markNum - 1) * 0.022)));
                                        const x = tickSide === 'left' ? 0.06 : 0.94;
                                        return {
                                            ...ann,
                                            page: Number(ann.page) >= 1 ? Number(ann.page) : currentPage,
                                            x,
                                            y,
                                            tickPoint: { x, y },
                                            needsPlacement: false,
                                            sourceCoordinate: 'teacher_auto_align',
                                        };
                                    }
                                    return ann;
                                });
                                onChange?.(updated);
                            }}
                        >
                            Place all on margin
                        </button>
                    </div>
                    <ul className="marking-suggestion-list">
                        {unplacedAnnotations.map(({ item: annotation, index }) => {
                            const isPlacing = placingAnnotationIndex === index;
                            const isLost = annotation.awarded === false || annotation.status === 'not_awarded' || annotation.type === 'lost';
                            return (
                                <li key={`unplaced-${annotation.questionNumber}-${annotation.markNumber}-${index}`} className="needs-placement">
                                    <span>
                                        <strong>Question {annotation.questionNumber || '-'} (Mark {annotation.markNumber || 1})</strong>
                                        {' — '}
                                        {isLost ? 'Not awarded (×)' : 'Awarded (✓)'}: {annotation.reason || 'Click place on paper'}
                                    </span>
                                    <span>
                                        <button
                                            type="button"
                                            className={isPlacing ? 'selected' : ''}
                                            onClick={() => {
                                                if (pdfFile && Number(annotation.page) >= 1) {
                                                    setCurrentPage(Number(annotation.page));
                                                }
                                                setPlacingAnnotationIndex(isPlacing ? null : index);
                                            }}
                                        >
                                            {isPlacing ? 'Click on paper now…' : 'Place on paper'}
                                        </button>
                                        <button type="button" className="reject" onClick={() => rejectSuggestion(index)}>Dismiss</button>
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
            <p className="marking-paper-note">
                {readOnly ? 'Script preview. Marking tools appear when the results are ready.' : rubricStamps.length > 0 ? `${rubricStamps.length} rubric stamp${rubricStamps.length === 1 ? '' : 's'} with compact examiner marks in reserved margins. Manual ticks can still be added.` : pendingSuggestions > 0 || unplacedAnnotations.length > 0
                    ? `${pendingSuggestions} AI tick suggestion${pendingSuggestions === 1 ? '' : 's'} and ${unplacedAnnotations.length} unplaced mark${unplacedAnnotations.length === 1 ? '' : 's'} still need teacher review. Drag any tick to move it, double-click to switch ✓/×, or right-click to delete.`
                    : `${placedAnnotations.length} mark position${placedAnnotations.length === 1 ? '' : 's'} on the paper. Drag any tick to move it, double-click to switch ✓/×, or right-click to delete.`}
            </p>
            {downloadState.error && <p className="marking-error">{downloadState.error}</p>}
            {pdfFile && objectUrl && (
                <a className="marking-paper-open-link" href={objectUrl} target="_blank" rel="noreferrer">
                    Download or open the original PDF
                </a>
            )}
        </section>
    );
}
